import React, { useMemo } from 'react';
import { Box, Grid } from '@mui/material';
import { useI18nContext } from '@procore/core-react';
import { Business, License } from '../../types/business';
import { Metrics } from '../../types/metrics-types';
import BreadcrumbContainer, {
  BreadcrumbItem,
} from '../Breadcrumbs/BreadcrumbContainer';
import BusinessProfileHeader from './BusinessProfileHeader';
import BusinessProfileNav from './BusinessProfileNav';
import BusinessProfileAbout from './BusinessProfileAbout';
import AreaOfCoverage from '../CoverageAreas/AreaOfCoverage';
import BusinessProfileLicenses from './BusinessProfileLicenses';
import BusinessProfileActivity from './BusinessProfileActivity';
import { GeoInfo } from '../../services/geo-info';
import AdBanner from './AdBanner';
import SimilarBusinesses from './SimilarBusinesses';

interface Props {
  business: Business;
  similarBusinesses: Business[];
  breadcrumbs: BreadcrumbItem[];
  metrics: Metrics;
  geoInfo: GeoInfo;
  claimBusinessProfile: boolean;
  adBanner: string;
  licenses: License[];
}

const AdBannerVariantA = () => {
  const I18n = useI18nContext();
  return (
    <AdBanner
      headline={I18n.t(
        'pages.p.business_profile_layout.ad_banner_variant_a.headline'
      )}
      subHeadline={I18n.t(
        'pages.p.business_profile_layout.ad_banner_variant_a.subHeadline'
      )}
      text={I18n.t('pages.p.business_profile_layout.ad_banner_variant_a.text')}
      adImagePath="/business-profile/ad-image-1.png"
      imageStyle="adImageVariantA"
      link="https://www.procore.com/project-management?utm_medium=banner&utm_source=businesspage&utm_campaign=pcn-pql-24"
      dataTrackClick={`${I18n.t(
        'pages.p.business_profile_layout.ad_banner_variant_a.headline'
      )} learn more ad banner`}
      analyticsLabel={`${I18n.t(
        'pages.p.business_profile_layout.ad_banner_variant_a.headline'
      )}`}
      data-test-id="ad-banner-variant-a"
    />
  );
};

const AdBannerVariantB = () => {
  const I18n = useI18nContext();
  return (
    <AdBanner
      headline={I18n.t(
        'pages.p.business_profile_layout.ad_banner_variant_b.headline'
      )}
      subHeadline={I18n.t(
        'pages.p.business_profile_layout.ad_banner_variant_b.subHeadline'
      )}
      text={I18n.t('pages.p.business_profile_layout.ad_banner_variant_b.text')}
      adImagePath="/business-profile/ad-image-2.png"
      imageStyle="adImageVariantB"
      link="https://www.procore.com/bid-management?utm_medium=banner&utm_source=businesspage&utm_campaign=pcn-pql-24"
      dataTrackClick={`${I18n.t(
        'pages.p.business_profile_layout.ad_banner_variant_b.headline'
      )} learn more ad banner`}
      analyticsLabel={`${I18n.t(
        'pages.p.business_profile_layout.ad_banner_variant_b.headline'
      )}`}
      data-test-id="ad-banner-variant-b"
    />
  );
};

const BusinessProfileLayout = ({
  business,
  similarBusinesses,
  breadcrumbs,
  metrics,
  geoInfo,
  claimBusinessProfile,
  adBanner,
  licenses,
}: Props) => {
  const profileBreadcrumb = useMemo(
    () => (
      <BreadcrumbContainer crumbs={[...breadcrumbs, { name: business.name }]} />
    ),
    [breadcrumbs]
  );
  let totalNumOfMetrics;
  if (business?.payingAccount === false) {
    totalNumOfMetrics = [
      metrics?.freemiumTotalProjects !== undefined &&
        metrics?.freemiumTotalProjects > 0,
      metrics?.freemiumTotalActiveProjects !== undefined &&
        metrics?.freemiumTotalActiveProjects > 0,
      metrics?.freemiumAvgProjectSize !== undefined &&
        metrics?.freemiumAvgProjectSize > 0,
      metrics?.freemiumNumOfEmployees !== undefined &&
        metrics?.freemiumNumOfEmployees > 0,
    ].filter(Boolean).length;
  } else {
    totalNumOfMetrics = [
      metrics?.totalProjects !== undefined && metrics?.totalProjects > 0,
      metrics?.numberOfOpenRfi !== undefined && metrics?.numberOfOpenRfi > 0,
      metrics?.activeProjects !== undefined && metrics?.activeProjects > 0,
      metrics?.submittalsCompleted !== undefined &&
        metrics?.submittalsCompleted > 0,
      metrics?.rfisCompleted !== undefined && metrics?.rfisCompleted > 0,
      metrics?.averageTimeToSubmittal !== undefined &&
        metrics?.averageTimeToSubmittal > 0,
    ].filter(Boolean).length;
  }

  const displayAbout = shouldDisplayAbout(business);
  const displayCoverageArea = shouldDisplayCoverageArea(business);
  const displayLicense = shouldDisplayLicenses(licenses);
  const displayMetricsCard = shoudlDisplayMetrics(metrics, totalNumOfMetrics);
  const displayNavBar =
    displayAbout || displayCoverageArea || displayLicense || displayMetricsCard;

  return (
    <Box data-test-id="business-profile-container" style={{ height: '100%' }}>
      <Grid item xs={12}>
        <Box pr={3}>{profileBreadcrumb}</Box>
      </Grid>
      <BusinessProfileHeader
        business={business}
        claimBusinessProfile={claimBusinessProfile}
      />
      {displayNavBar && (
        <BusinessProfileNav
          displayAbout={displayAbout}
          displayCoverageArea={displayCoverageArea}
          displayLicense={displayLicense}
          displayMetricsCard={displayMetricsCard}
        />
      )}
      {displayAbout && (
        <div id="about">
          <BusinessProfileAbout business={business} />
        </div>
      )}
      {displayCoverageArea && (
        <div id="service-areas">
          <AreaOfCoverage business={business} location={geoInfo?.center} />
        </div>
      )}
      {displayLicense && (
        <div id="licenses">
          <BusinessProfileLicenses
            businessName={business.name}
            licenses={licenses}
          />
        </div>
      )}
      {displayMetricsCard && (
        <div id="procore-activity">
          <BusinessProfileActivity business={business} metrics={metrics} />
        </div>
      )}
      {adBanner === 'A' && <AdBannerVariantA />}
      {adBanner === 'B' && <AdBannerVariantB />}
      {similarBusinesses?.length > 0 && (
        <SimilarBusinesses similarBusinesses={similarBusinesses} />
      )}
    </Box>
  );
};

function shouldDisplayAbout(business: Business) {
  return (
    business.companySize !== null ||
    business.avgContractSize !== null ||
    (business.monolithCreatedAt !== null &&
      business.monolithCreatedAt !== '') ||
    business.providedServices?.length > 0 ||
    business.constructionSectors?.length > 0 ||
    business.businessTypes?.length > 0 ||
    business.classifications?.length > 0
  );
}

function shouldDisplayCoverageArea(business: Business) {
  return business.coverageAreas?.length > 0;
}

function shouldDisplayLicenses(licenses: License[]) {
  return licenses?.length > 0;
}

function shoudlDisplayMetrics(metrics: Metrics, totalNumOfMetrics: number) {
  return metrics && totalNumOfMetrics !== 0;
}
export default BusinessProfileLayout;
